/**
 * permission-areas — podział katalogu uprawnień na obszary Centrum (F24 Kawałek 1).
 *
 * Centrum ma cztery zakładki, a katalog ~200 kodów. Trzy z tych zakładek biorą kody stąd:
 *   • KARTOTEKI — kody objęte drzewem obiektów (dokumenty, kontrahenci, pracownicy, towary, zadania).
 *     Nie wypisujemy ich na liście płaskiej, bo nadaje się je z hierarchią; zbiór wyliczamy z
 *     samego drzewa, więc nie może się rozjechać z tym, co pokazuje tamta zakładka.
 *   • ADMINISTRACYJNE — konfiguracja systemu: kto może zmieniać ustawienia, słowniki, role,
 *     moduły, integracje. Prawa, które zwykły pracownik dostaje wyjątkowo.
 *   • OGÓLNE — cała reszta: codzienna praca, której nie da się przypiąć do kartoteki (procesy,
 *     pulpit, widgety, magazyny).
 *
 * Podział obszaru administracyjnego trzymamy w JEDNYM miejscu — liście kategorii poniżej —
 * właśnie po to, żeby przesunięcie obszaru między zakładkami było zmianą jednej linijki, a nie
 * polowaniem po komponentach. Flaga `isAdminOnly` z katalogu bierze udział w decyzji, ale sama
 * nie wystarcza: ma ją tylko garstka kodów, a „ustawienia firmy" czy „słowniki" są równie
 * administracyjne, mimo że flagi nie mają.
 *
 * Obok podziału: etykieta poziomu reguły w oknie „skąd to wynika" (`ruleLevelOf`, z rodzaju
 * i klucza obiektu reguły z serwera) i ścieżka obiektu komórki siatki (`cellObjectPath`, liczona
 * jak ścieżka rekordu na serwerze).
 */

import {
  OUT_OF_PROCESS_STAGE_KEY,
  type PermissionObjectKind,
  type PermissionObjectRef,
  stagePathRefs,
} from '@saas/shared';

/** Kategorie katalogu uznane za konfigurację systemu. */
const ADMIN_CATEGORIES = new Set([
  'roles',
  'users',
  'companies',
  'dictionaries',
  'document_types',
  'task_types',
  'menu_layouts',
  'system_modules',
  'custom_modules',
  'widgets',
  'chart_of_accounts',
  'record_procedures',
  'non_working_days',
  'processes_procedures',
  'processes_templates',
  'processes_definitions',
  'symfonia_integration',
  'ksef_integration',
]);

export type PermissionArea = 'ownRecords' | 'modules' | 'general' | 'admin';

export interface CatalogDefinition {
  code: string;
  moduleCode: string;
  name: string | null;
  description: string | null;
  category: string | null;
  isAdminOnly: boolean;
}

/**
 * Prawa do REKORDÓW modułu własnego (`crm_core.module.<moduł>.view|edit|delete`). Katalog trzyma
 * je w kategorii `custom_modules` razem z prawem do budowania modułów, ale to codzienna praca —
 * ktoś wpisuje badania lekarskie albo wypadki, nie konfiguruje systemu.
 *
 * Trafiają do zakładki KARTOTEKI WŁASNE, nie do „Modułów": moduł własny jest w istocie kartoteką
 * zbudowaną przez klienta — ma własną tabelę, kategorie i formatkę, a prawa nadaje się do jego
 * REKORDÓW, dokładnie jak przy kontrahentach czy dokumentach. Zakładka „Moduły" zostaje dla tego,
 * co kartoteką nie jest (Handel i magazyn, Magazyny, Procesy).
 */
const MODULE_RECORD_PREFIX = 'crm_core.module.';

/**
 * Moduły dokupowane, które NIE są kartoteką — mają własną logikę, a nie zbiór rekordów
 * z formatką. Rozpoznajemy je po module katalogu, bo to jedyna rzecz, która je łączy.
 *
 * Integracje (Symfonia, KSeF) świadomie tu NIE wchodzą — to konfiguracja systemu i zostają
 * w uprawnieniach administracyjnych.
 */
const STANDALONE_MODULE_CODES = new Set(['handel_magazyn', 'warehouses', 'processes']);

/** Slug modułu wyjęty z kodu: `crm_core.module.bhp_wypadki.view` → `bhp_wypadki`. */
export function moduleSlugOf(code: string): string | null {
  if (!code.startsWith(MODULE_RECORD_PREFIX)) return null;
  const rest = code.slice(MODULE_RECORD_PREFIX.length);
  const dot = rest.lastIndexOf('.');
  return dot === -1 ? rest : rest.slice(0, dot);
}

/**
 * Przedrostek klucza kartoteki modułu w drzewie — lustro stałej serwerowej z
 * `lib/permissions/object-map`. Panel nie ma dostępu do kodu serwera, a klucz przychodzi
 * w drzewie jako zwykły napis.
 */
const CUSTOM_KARTOTEKA_PREFIX = 'custom:';

/**
 * Czy węzeł drzewa należy do zakładki KARTOTEKI WŁASNE: kartoteka modułu utworzona przez
 * tenanta. Kartoteka modułu zainstalowana przez ITE (`installedByIte`) jest dla tenanta
 * systemowa — zostaje w zakładce „Kartoteki" razem z kontrahentami czy dokumentami.
 */
export function isOwnKartotekaNode(node: { key: string; installedByIte?: boolean }): boolean {
  return node.key.startsWith(CUSTOM_KARTOTEKA_PREFIX) && !node.installedByIte;
}

/** Klucz kartoteki Zadania w drzewie — lustro stałej serwerowej. */
export const TASKS_KARTOTEKA_KEY = 'tasks';

/**
 * Kartoteki do wyboru w regule warunkowej: tylko te, które serwer oznaczył w drzewie jako
 * `conditionable` (wybór innej skończyłby się odrzuceniem zapisu przez serwer).
 */
export function selectConditionKartoteki<T extends { kind: string; conditionable?: boolean }>(
  nodes: readonly T[],
): T[] {
  return nodes.filter((node) => node.kind === 'kartoteka' && node.conditionable === true);
}

/** Poziom reguły w etykiecie okna „skąd to wynika" (`ruleLevelOf`). */
export type RuleLevel = PermissionObjectKind | 'task_type' | 'out_of_process';

/**
 * Poziom reguły do etykiety „na jakim poziomie obiektu to ustawiono". Typ zadania to w drzewie
 * ten sam rodzaj węzła co typ dokumentu (`doc_type`), ale z kluczem kartoteki Zadania — dla
 * człowieka to inny poziom, więc rozróżniamy go po kluczu. Tak samo „poza procesem": w regule to
 * etap (`process_step`) z kluczem zarezerwowanym, a dla człowieka — stan „bez procesu", nie etap.
 */
export function ruleLevelOf(
  objectKind: PermissionObjectKind,
  objectKey?: string | null,
): RuleLevel {
  if (objectKind === 'doc_type' && objectKey === TASKS_KARTOTEKA_KEY) return 'task_type';
  if (objectKind === 'process_step' && objectKey === OUT_OF_PROCESS_STAGE_KEY) {
    return 'out_of_process';
  }
  return objectKind;
}

/** Węzeł drzewa w kształcie potrzebnym do złożenia ścieżki komórki (`cellObjectPath`). */
export interface CellPathNode {
  /** Rodzaj obiektu reguły albo `process` — grupa procesu, kontener bez własnego kodu. */
  kind: PermissionObjectKind | 'process';
  key: string;
  id?: string;
  /** Pusty = wiersz-kontener („Dokumenty", grupa procesu) — nie jest obiektem reguły. */
  codeNamespace: string;
}

/**
 * Ścieżka obiektu komórki Centrum — od kartoteki w dół, liczona jak ścieżka, o którą serwer pyta
 * silnik o rekord (`buildObjectPath`, `lib/permissions/object-map.ts`), z jednym wyjątkiem: poziomu
 * całej kartoteki Dokumenty. To kontener bez przełączników — panel nie nadaje na nim praw — więc
 * jego wiersz (jak inne wiersze-kontenery, np. grupa procesu) wypada ze ścieżki, choć serwer go na
 * niej ma.
 *
 * Poziom etapu składa wspólna z serwerem `stagePathRefs` (`@saas/shared`): przed etapem innym niż
 * „poza procesem" wchodzi „gdziekolwiek w obiegu" tego typu. W drzewie to RODZEŃSTWO etapów, ale
 * rekord stojący w obiegu przechodzi przez ten poziom, zanim dojdzie do swojego etapu — bez niego
 * zakaz „gdziekolwiek w obiegu" nie sięgałby wierszy etapów, a panel pokazywałby „dozwolone", choć
 * serwer odmawia. Dotyczy dokumentów i zadań.
 */
export function cellObjectPath(nodes: readonly CellPathNode[]): PermissionObjectRef[] {
  const path: PermissionObjectRef[] = [];
  for (const node of nodes) {
    if (node.kind === 'process' || node.codeNamespace === '') continue;
    if (node.kind === 'process_step') {
      path.push(...stagePathRefs(node.id ?? null, node.key));
      continue;
    }
    path.push({ kind: node.kind, key: node.key, id: node.id ?? null });
  }
  return path;
}

/** Do której zakładki trafia kod spoza drzewa kartotek. */
export function areaOf(def: CatalogDefinition): PermissionArea {
  if (def.code.startsWith(MODULE_RECORD_PREFIX)) return 'ownRecords';
  if (def.isAdminOnly) return 'admin';
  if (def.category != null && ADMIN_CATEGORIES.has(def.category)) return 'admin';
  // Moduł dokupowany sprawdzamy PO obszarach administracyjnych: konfiguracja procesów
  // (definicje, szablony, procedury) zostaje przy administracji, a przy module lądują jego
  // sprawy codzienne.
  if (STANDALONE_MODULE_CODES.has(def.moduleCode)) return 'modules';
  return 'general';
}
